"use client";

import { createContext, useCallback, useContext, useMemo, useRef, type ReactNode } from "react";

interface VideoMuteCoordinatorValue {
  register: (id: string, mute: () => void) => () => void;
  notifyUnmuted: (id: string) => void;
}

const VideoMuteCoordinatorContext = createContext<VideoMuteCoordinatorValue | null>(null);

export function VideoMuteCoordinator({ children }: { children: ReactNode }) {
  const videosRef = useRef(new Map<string, () => void>());

  const register = useCallback((id: string, mute: () => void) => {
    videosRef.current.set(id, mute);
    return () => {
      videosRef.current.delete(id);
    };
  }, []);

  const notifyUnmuted = useCallback((id: string) => {
    videosRef.current.forEach((mute, otherId) => {
      if (otherId !== id) mute();
    });
  }, []);

  const value = useMemo(() => ({ register, notifyUnmuted }), [register, notifyUnmuted]);

  return <VideoMuteCoordinatorContext.Provider value={value}>{children}</VideoMuteCoordinatorContext.Provider>;
}

export function useVideoMuteCoordinator() {
  return useContext(VideoMuteCoordinatorContext);
}
